import {
  getApolloSandboxLink,
  getRegisteredApolloClient,
  useApolloOperations
} from "@pathwright/utils/src/graphql/apolloOperation"
import { useMemo } from "react"
import PWAdminCmdKItem, { PWAdminCmdKItemProps } from "./PWAdminCmdKItem"

type Option = PWAdminCmdKItemProps & { option: { href: string } }

function PWAdminCmdKGraphQLOperations() {
  const operations = useApolloOperations()

  const links: Option[] = useMemo(() => {
    // Separating active and inactive operations so active ones appear first and
    // we retain order of operations as provided by the store.
    const [activeOperationLinks, inactiveOperationLinks] = operations.reduce(
      ([accActive, accInactive], operation) => {
        const developmentLink = getApolloSandboxLink(operation, true)
        const operatingEnvLink = getApolloSandboxLink(operation, false)

        // Only adding if operation not already added that matches same href.
        // Operations sharing the same href are exact duplicates.
        if (
          ![...accActive, ...accInactive].some(
            (link) => link.option.href === developmentLink
          )
        ) {
          // If an observed operation matches this operation name, consider it active.
          // Observed queries are those used by currently mounted useQuery hooks.
          const client = getRegisteredApolloClient(
            operation.graphqlPath || "/graphql"
          )
          const observedOperations = client
            ? [...client.getObservableQueries().values()]
            : []
          const isActive = observedOperations.some((observed) => {
            return observed.queryName === operation.operationName
          })

          const isBlocks = operation.graphqlPath === "/blocks/graphql"

          const link: Option = {
            option: {
              label: operation.operationName,
              icon: isBlocks ? "layout-blocks" : "house",
              // Conditionally indicating when an operation is active.
              iconProps: operation.hasError
                ? { color: "red.400" }
                : isActive
                ? { color: "green.400" }
                : undefined,
              href: developmentLink,
              link: {
                href: operatingEnvLink,
                icon: "globe-fill"
              }
            }
            // Supply any Command.Item props here if needed.
          }

          // If there are other operations with the same name, differentiate the label
          // by the unique variables.
          if (
            operations.some(
              (op) =>
                op.operationName === operation.operationName && op !== operation
            )
          ) {
            if (Object.keys(operation.variables).length) {
              link.option.label = `${
                operation.operationName
              } - ${JSON.stringify(operation.variables)}`
            }
          }

          if (isActive) accActive.push(link)
          if (!isActive) accInactive.push(link)
        }

        return [accActive, accInactive]
      },
      [[], []] as [Option[], Option[]]
    )

    return [...activeOperationLinks, ...inactiveOperationLinks]
  }, [operations])

  return links.map((link) => (
    <PWAdminCmdKItem key={link.option.href} {...link} />
  ))
}

export default PWAdminCmdKGraphQLOperations
